import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { SR_API_ACTION_GET_LISTING_UNITS } from 'api/actionTypes';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { LISTING_STATUS_ACTIVE, LISTING_PHOTO_DIMENSION_THUMBNAIL } from 'common/listingConstants';
import {
  getListingFieldsForDisplay,
  getListingUrlPath,
  getListingManagedPhotoUrls,
  getListingAvailableDateMoment,
} from 'common/listingHelpers';
import { numberWithCommas } from 'common/formatters';

import SrLink from 'client/components/SrLink/SrLink';
import ActivityIndicator from 'client/components/ActivityIndicator';
import branch from 'client/core/branch';
import ListingActions from 'client/actions/Listing';
import { makeListingsStateViewId } from 'client/actions/Listing/getUnits';
import withApiState from 'client/helpers/withApiState';
import {
  makeListingsStateManagerViewPath,
  actionInitializeListingsStateManagerViewMonkey,
} from 'client/helpers/listingsStateManager';

@withApiState(({ listing }) => ({
  getListingUnitsApiState: {
    action: SR_API_ACTION_GET_LISTING_UNITS,
    queryId: makeListingsStateViewId(listing),
  },
}))
@branch(({ listing }) => ({
  listingPhotoResolution: ['listingPhotoResolution'],
  units: makeListingsStateManagerViewPath(makeListingsStateViewId(listing)).concat(['$listings']),
  totalUnits: makeListingsStateManagerViewPath(makeListingsStateViewId(listing)).concat(['total']),
}))
class SrListingMoreUnits extends Component {
  static propTypes = {
    listingPhotoResolution: PropTypes.number.isRequired,
    listing: PropTypes.object.isRequired,
    units: PropTypes.array,
    totalUnits: PropTypes.number,
    dispatch: PropTypes.func.isRequired,
    getListingUnitsApiState: PropTypes.object.isRequired,
  };

  static defaultProps = {
    units: [],
  };

  constructor(props) {
    super(props);

    this.state = this._getInitialState(props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.totalUnits !== nextProps.totalUnits ||
      this.props.listing.id !== nextProps.listing.id
    ) {
      this.setState(this._getInitialState(nextProps));
    }
  }

  componentDidMount() {
    const { dispatch, listing, totalUnits } = this.props;
    if (totalUnits) {
      dispatch(actionInitializeListingsStateManagerViewMonkey, {
        viewId: makeListingsStateViewId(listing),
      });
    }
  }

  _getInitialState = (props) => {
    return {
      showAllUnits: props.totalUnits < 4,
    };
  };

  _onShowAllUnits = () => {
    const { dispatch, listing } = this.props;

    this.setState({
      showAllUnits: true,
    });

    dispatch(ListingActions.getUnits, { listing, loadMoreUnits: true });
  };

  render() {
    const { listingPhotoResolution, listing, totalUnits, getListingUnitsApiState } = this.props;
    const { showAllUnits } = this.state;

    const allUnits = this.props.units.filter((unit) => unit.id !== listing.id);
    if (!allUnits.length) {
      return null;
    }

    const unitsToShow = showAllUnits ? allUnits : allUnits.slice(0, 3);
    const todayMoment = momentWithAustinTimezone().startOf('day');

    return (
      <div className="SrListingMoreUnits section">
        <div className="container">
          <div className="listView">
            <span className="headerText">
              {allUnits.length} {pluralize('other unit', allUnits.length)} in this building
            </span>
            <div>
              {unitsToShow.map((listing) => {
                const managedPhotoUrls = getListingManagedPhotoUrls({
                  listing: listing,
                  dimension: LISTING_PHOTO_DIMENSION_THUMBNAIL,
                  resolution: listingPhotoResolution,
                });

                const listingFieldsForDisplay = getListingFieldsForDisplay({ listing });
                const unitId = listing.addressStreetDisplay.split(' ').pop();
                const isLeased = listing.status !== LISTING_STATUS_ACTIVE;
                let availableText = 'Leased';
                if (!isLeased) {
                  const availableDateMoment = getListingAvailableDateMoment(listing);
                  const isAvailabeNow = !availableDateMoment.isAfter(todayMoment);
                  availableText = `Available ${
                    isAvailabeNow ? 'now' : availableDateMoment.format('M/D')
                  }`;
                }

                return (
                  <SrLink key={listing.id} className="listing" to={getListingUrlPath(listing)}>
                    <img
                      className="thumbnail"
                      src={managedPhotoUrls[0]}
                      alt={listing.addressStreetDisplay}
                    />
                    <div className="listingInfo">
                      <div className="description">
                        <span className="unit">
                          #{unitId}
                          <span className={isLeased ? 'leased' : 'available'}>{availableText}</span>
                        </span>
                        <div className="details">
                          <span>${numberWithCommas(listingFieldsForDisplay.price)}</span>
                          <span className="pipe"> | </span>
                          <span>{listingFieldsForDisplay.bedrooms} bed</span>
                          <span className="pipe"> | </span>
                          <span>{listingFieldsForDisplay.bathrooms} bath</span>
                        </div>
                      </div>
                      <div className="link">
                        <span>View</span>
                      </div>
                    </div>
                  </SrLink>
                );
              })}
            </div>
            {getListingUnitsApiState.inProgress && (
              <ActivityIndicator className="activiyIndicator" />
            )}
          </div>
          {!showAllUnits && (
            <div className="viewMoreUnits" onClick={this._onShowAllUnits}>
              <span>+ View all {totalUnits - 1} units</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}

SrListingMoreUnits.displayName = 'SrListingMoreUnits';

export default SrListingMoreUnits;
